import React from "react";
import {Box, Flex, Heading, Text, Spinner, Button, Icon, Badge} from "@chakra-ui/react";
import {FiAlertCircle, FiLock, FiRefreshCw, FiUserX} from "react-icons/fi";
import {SECTION_STATES} from "./useDependentSection";

// The frame every dependent screen sits in.
//
// One frame rather than six, because the three failure states — loading, not
// available, could not load — are where a screen most easily says something
// untrue. "Not available" and "empty" look identical if each screen invents its
// own empty state, and confusing the two is how a guardian concludes their
// child has no appointments when what actually happened is that they may not
// see them.

const LEVEL_TONE = {
  full: null,
  limited: {colorScheme: "blue", label: "Limited access"},
  restricted: {colorScheme: "orange", label: "Restricted access"},
};

export default function DependentShell({
  title,
  subtitle,
  displayName,
  accessLevel,
  status,
  error,
  onRetry,
  onLeave,
  actions,
  children,
}) {
  const tone = LEVEL_TONE[accessLevel];

  return (
    <Box px={{base: 4, md: 8}} py={{base: 4, md: 6}} maxW="1100px" mx="auto" w="full">
      <Flex
        justify="space-between"
        align={{base: "flex-start", md: "center"}}
        direction={{base: "column", md: "row"}}
        gap={3}
        mb={5}
      >
        <Box minW={0}>
          <Flex align="center" gap={2} wrap="wrap">
            <Heading size="md" color="#1a1f36">
              {title}
            </Heading>
            {tone && (
              <Badge colorScheme={tone.colorScheme} fontSize="10px" textTransform="none">
                {tone.label}
              </Badge>
            )}
          </Flex>
          <Text fontSize="sm" color="gray.600" mt={1}>
            {subtitle || `${displayName}'s record`}
          </Text>
        </Box>
        {actions}
      </Flex>

      {status === SECTION_STATES.LOADING && (
        <Flex justify="center" align="center" py={16} direction="column" gap={3}>
          <Spinner thickness="3px" color="orange.400" emptyColor="gray.200" size="lg" />
          <Text fontSize="sm" color="gray.500">
            Loading…
          </Text>
        </Flex>
      )}

      {status === SECTION_STATES.UNAVAILABLE && (
        <Notice
          icon={FiLock}
          tone="gray"
          heading="Not available to you"
          body={`Your clinic has not given you access to this part of ${displayName}'s record. If you think that is wrong, please contact the clinic — access is set by staff and cannot be changed here.`}
        />
      )}

      {status === SECTION_STATES.REVOKED && (
        <Notice
          icon={FiUserX}
          tone="gray"
          heading={`You no longer have access to ${displayName}`}
          body={`Your clinic has ended this access, so nothing on ${displayName}'s record is available to you here. Anything the clinic already showed you has gone with it.`}
          action={
            onLeave && (
              <Button size="sm" mt={4} colorScheme="orange" onClick={onLeave}>
                Back to my profile
              </Button>
            )
          }
        />
      )}

      {status === SECTION_STATES.ERROR && (
        <Notice
          icon={FiAlertCircle}
          tone="red"
          heading="This could not be loaded"
          body={error}
          action={
            onRetry && (
              <Button
                size="sm"
                mt={4}
                leftIcon={<Icon as={FiRefreshCw} />}
                variant="outline"
                onClick={onRetry}
              >
                Try again
              </Button>
            )
          }
        />
      )}

      {status === SECTION_STATES.READY && children}
    </Box>
  );
}

function Notice({icon, tone, heading, body, action}) {
  return (
    <Flex
      direction="column"
      align="center"
      textAlign="center"
      py={14}
      px={6}
      bg={tone === "red" ? "red.50" : "gray.50"}
      border="1px solid"
      borderColor={tone === "red" ? "red.100" : "gray.200"}
      borderRadius="12px"
    >
      <Icon as={icon} fontSize="28px" color={tone === "red" ? "red.400" : "gray.400"} mb={3} />
      <Text fontWeight="600" color="#1a1f36" mb={1}>
        {heading}
      </Text>
      <Text fontSize="sm" color="gray.600" maxW="480px">
        {body}
      </Text>
      {action}
    </Flex>
  );
}

/** An honest empty state: there is nothing here, and you would have seen it. */
export function EmptyState({heading, body}) {
  return (
    <Flex
      direction="column"
      align="center"
      textAlign="center"
      py={12}
      px={6}
      border="1px dashed"
      borderColor="gray.200"
      borderRadius="12px"
    >
      <Text fontWeight="600" color="#1a1f36" mb={1}>
        {heading}
      </Text>
      <Text fontSize="sm" color="gray.600" maxW="460px">
        {body}
      </Text>
    </Flex>
  );
}

/**
 * Said out loud wherever a list is narrowed to what this guardian did.
 *
 * A `book_only` guardian sees the appointments they booked and no others, and a
 * list that silently omits rows is worse than one that explains itself: without
 * this line the guardian's conclusion is "there are no other appointments",
 * which is a statement about the child that the screen has no basis to make.
 */
export function ScopeNote({scope, noun}) {
  if (scope !== "own") return null;
  return (
    <Text fontSize="xs" color="gray.500" mt={3}>
      You are seeing only the {noun} you handled yourself. Others may exist that your access does
      not cover.
    </Text>
  );
}
